// S2 · Step 1 — Vertical picker copy (Module 02 §4 S2, §3.2).
//
// Lives with the screen so the builder adds keys where they work; it is spliced
// into the `onboarding` namespace as `onboarding.vertical.*` by
// ../../onboarding.i18n.ts, which is what carries it into the four-locale CI
// parity gate (`pnpm run i18n:parity`, D-014). ru is TRANSLATED (vertical.ru.ts);
// zh/fr still render English — declared in src/i18n-completeness.ts.
//
// Two rules the copy obeys:
//  * No entity NOUN is written here. Nouns that a tenant can rename (work
//    center, stock unit, …) resolve through `useTerm()` at render time (§2.5).
//  * Nothing claims an object exists unless it does. §3.2 requires a kind whose
//    owning module is unwired to read as PENDING that module, never as
//    installed — hence the separate `breakdown.skipped*` copy.

export const verticalEn = {
  // ---- header / page-level -------------------------------------------------
  packsHeading: "Industry packs",
  packsSubtitle: "Your industry's steps, units, checks and words. Preview before you install.",
  loading: "Loading industry packs…",
  errorTitle: "Could not load the industry packs",
  errorDetail: "The pack catalogue could not be read. Try again.",
  emptyTitle: "No industry packs are available",
  emptyBody:
    "No pack is offered for you yet. Contact Assessly. You can continue setup, but step 1 needs a pack.",
  readOnlyNote: "Only a group admin can install an industry pack. You can still preview each one.",
  frozenNote: "Your factory is live. Packs can no longer be installed here.",
  completeHint: "Install a pack first — step 1 cannot be completed without one.",

  // ---- pack card -----------------------------------------------------------
  card: {
    version: "Version {{version}}",
    objects: "{{count}} objects in this pack",
    countLine: "{{count}} {{kind}}",
    unnamed: "Untitled pack",
    noDescription: "This pack ships no description.",
    previewCta: "Preview pack",
    installCta: "Install this pack",
    switchCta: "Switch to this pack",
    installedBadge: "Installed",
    installingBadge: "Installing",
    failedBadge: "Install failed",
  },

  // ---- the current install (§3.2) -----------------------------------------
  current: {
    loading: "Checking your pack install…",
    errorTitle: "Could not read the current pack install",
    errorDetail: "Your install status could not be read, so the buttons below may be out of date.",
    installedTitle: "{{name}} is installed",
    installedBody: "Version {{version}}, finished {{date}}.",
    installingTitle: "Installing {{name}}…",
    installingBody:
      "The pack is being seeded into your factory. This page updates itself; you can leave and come back.",
    progress: "{{done}} of {{total}} objects created",
    failedTitle: "Installing {{name}} failed",
    failedBody:
      "Nothing was rolled back — everything already created stays, and a retry finishes the rest.",
    failedCode: "Error code: {{code}}",
    supersededTitle: "{{name}} was switched away",
    supersededBody: "Your factory has no active pack right now. Install one to continue.",
    switchCta: "Switch pack",
    retryCta: "Retry the install",
    finishedUnknown: "just now",
    // Raw server codes are a support detail, never the explanation (i18n rule).
    technicalDetail: "Technical detail",
  },

  // ---- honest per-kind breakdown (§3.2) ------------------------------------
  breakdown: {
    heading: "What this pack put in your factory",
    seededHeading: "Created in your factory",
    seededNote: "These exist as real records you can open and edit in their own screens.",
    snapshotHeading: "Kept with your pack",
    snapshotNote:
      "These are templates stored with your installed pack. They are used when you compose routings and lay out your plant; they are not separate records.",
    skippedHeading: "Waiting on other modules",
    skippedNote:
      "Not created: their module isn't in use here. They're added from your pack once it is.",
    colKind: "Kind",
    colInPack: "In the pack",
    colCreated: "Created",
    colOwner: "Owned by",
    pendingModule: "Pending {{module}}",
    inSnapshot: "Stored with the pack",
    created: "{{done}} of {{total}}",
    totals:
      "{{materialized}} records created, {{snapshot}} kept with the pack, {{pending}} waiting on other modules.",
  },

  // Pack object kinds (spec 02 §2.4 pack_seed_links.object_kind).
  kinds: {
    unit: "Units of measure",
    attribute_schema: "Attribute schemas",
    catalogue_template: "Catalogue templates",
    qc_checklist_template: "Quality checklists",
    failure_cause: "Failure causes",
    waste_category: "Waste categories",
    waste_cause: "Waste causes",
    remnant_price_class: "Remnant price classes",
    waste_settings: "Waste settings",
    downtime_reason: "Downtime reasons",
    maintenance_reason_code: "Maintenance reason codes",
    // Module 09's two kinds. `offcut_discount_class` shipped at P3-A with no
    // label here, so the breakdown printed the raw code; `pipeline_stage`
    // arrives with P3-E2 PR E2-1 (D-312).
    offcut_discount_class: "Off-cut discount classes",
    pipeline_stage: "Pipeline stages",
    role: "Roles",
    terminology_bundle: "Vocabulary",
    routing_step_template: "Routing step templates",
    work_center_template: "Work center templates",
    machine_type: "Machine types",
    label_template: "Label templates",
  },

  // ---- install confirm (§4 S2 "Install button with confirm") ---------------
  confirm: {
    installTitle: "Install {{name}}?",
    installBody:
      "Installing seeds your factory with everything in this pack. It runs in the background and you can keep working.",
    switchTitle: "Switch to {{name}}?",
    switchBody:
      "You already have {{current}} installed. Switching changes your factory's defaults.",
    consequencesHeading: "Before you switch, know that:",
    consequenceSupersede: "Your current pack is marked superseded and the new one is seeded.",
    consequenceAdopt: "Anything both packs share — a unit, a role — is reused, not duplicated.",
    consequenceOrphans:
      "Records only the old pack introduced stay in place and are flagged for you to review; nothing is deleted automatically.",
    consequenceAfterLive: "Packs cannot be switched once your factory is live.",
    confirmInstall: "Install pack",
    confirmSwitch: "Switch pack",
    cancel: "Cancel",
    working: "Starting…",
  },

  // ---- install failures (§5 problem codes) ---------------------------------
  problem: {
    alreadyInstalledTitle: "A pack is already installed",
    alreadyInstalledBody:
      "Your factory already has an active pack. Use “Switch pack” to replace it, so the old one is retired rather than two running at once.",
    unknownTitle: "That pack no longer exists",
    unknownBody:
      "The pack catalogue changed while this page was open. Reload the list and pick again.",
    unknownReload: "Reload the pack list",
    failedTitle: "The install could not be started",
    failedDetail: "Nothing was changed. Try again.",
  },

  // ---- preview drawer (§4 S2 tabs) ----------------------------------------
  drawer: {
    ariaLabel: "Pack preview",
    close: "Close",
    loading: "Loading the pack…",
    errorTitle: "Could not load this pack",
    errorDetail: "The pack contents could not be read. Try again.",
    version: "Version",
    currency: "Suggested currency",
    currencyNote:
      "The pack's suggested currency. Installing a pack doesn't change your own currency.",
    measuredKinds: "Measured stock units",
    none: "None",
    tabs: {
      steps: "Steps",
      units: "Units",
      qc: "Quality",
      waste: "Waste",
      terminology: "Vocabulary",
    },
    steps: {
      colRef: "Reference",
      colName: "Step",
      colWorkCenter: "Work center template",
      colSetup: "Setup",
      colRun: "Run rate",
      colScrap: "Expected scrap",
      colCaptures: "Captured on the floor",
      colQc: "Quality check",
      setupMinutes: "{{minutes}} min",
      runRate: "{{seconds}} s per {{unit}}",
      scrapPct: "{{pct}}%",
      emptyTitle: "This pack ships no routing steps",
      emptyBody: "You will build your routings from scratch in step 2.",
    },
    units: {
      colCode: "Code",
      colName: "Name",
      colKind: "Measures",
      colPrecision: "Decimals",
      emptyTitle: "This pack ships no units",
      emptyBody: "Your existing units of measure are untouched.",
    },
    qc: {
      colRef: "Reference",
      colName: "Checklist",
      colControlPoint: "Checked at",
      colItems: "Checks",
      colFailAction: "On failure",
      colGuidedCut: "Guided cut",
      itemCount: "{{count}} checks",
      itemCount_one: "{{count}} check",
      range: "{{min}}–{{max}} {{unit}}",
      yes: "Yes",
      no: "No",
      emptyTitle: "This pack ships no quality checklists",
      emptyBody: "You can build your own once the quality module is switched on.",
    },
    waste: {
      colRef: "Reference",
      colName: "Category",
      colDisposition: "Default disposition",
      colRecoverable: "Recoverable",
      colDimensions: "Tracks dimensions",
      yes: "Yes",
      no: "No",
      emptyTitle: "This pack ships no waste categories",
      emptyBody: "You can add your own once the waste module is switched on.",
    },
    terminology: {
      colKey: "Term",
      colLabel: "This pack calls it",
      colOtherLocales: "Other languages",
      emptyTitle: "This pack keeps the standard vocabulary",
      emptyBody:
        "Nothing is renamed. You can still rename anything yourself in terminology settings.",
      note: "Installing renames these across every screen. You can change any of them later in terminology settings.",
    },
  },

  // Control points / dispositions / captures the pack uses, so the drawer never
  // renders a raw enum value at a user.
  controlPoint: {
    receiving: "Goods receipt",
    in_process: "In process",
    final: "Final inspection",
  },
  failAction: {
    rework: "Rework",
    scrap: "Scrap",
    quarantine: "Quarantine",
    cut_and_retest: "Cut and retest",
  },
  disposition: {
    reuse: "Reuse",
    regrind: "Regrind",
    sell: "Sell",
    dispose: "Dispose",
    pending: "Decide later",
  },
  unitKind: {
    length: "Length",
    mass: "Mass",
    area: "Area",
    count: "Count",
    volume: "Volume",
    time: "Time",
    container: "Container",
  },
  capture: {
    qty_good: "Good quantity",
    qty_scrap: "Scrap quantity",
    downtime_reason: "Downtime reason",
    measured_length: "Measured length",
    claimed_length: "Claimed length",
    batch_ref: "Batch reference",
    shift_handoff_note: "Shift handover note",
    photo: "Photo",
  },
  measuredKind: {
    barrel: "Barrel",
    bobbin: "Bobbin",
    roll: "Roll",
    coil: "Coil",
    pallet: "Pallet",
    bag: "Bag",
    none: "None",
  },
} as const;
